import React, { useRef } from 'react'
import { Checkbox, Col, Form, Modal, Radio, Row, Typography } from 'antd'
import { Formik, FormikProps } from 'formik'
import { object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import Item from 'antd/lib/list/Item'

import { Input, InputCurrency, Select } from 'src/components/Formik'
import { Policy, PolicyAction, PolicyOperator } from 'src/types'
import { Title } from 'src/components/Ant'

import * as CSS from './styles'
import {
  ButtonAddNew,
  PolicyRuleAlertIcon,
  RecordText,
  SquareIcon,
  tOperator
} from '../../utils'

interface ServiceRulesProps {
  formikProps: FormikProps<Policy>
  keyPrefix: string
  policyRuleIndex: number
}

export default function ServiceRules({
  formikProps,
  keyPrefix = 'air',
  policyRuleIndex
}: ServiceRulesProps) {
  const { t } = useTranslation()
  const refFormik = useRef(null)

  const thisPolicy = formikProps.values?.policy_rules?.[policyRuleIndex]

  if (
    (keyPrefix === 'air' && !thisPolicy.enable_flight) ||
    (keyPrefix === 'hotel' && !thisPolicy.enable_hotel) ||
    (keyPrefix === 'vehicle' && !thisPolicy.enable_vehicle_rent)
  ) {
    return <div />
  }

  const polReqRule = `policy_request_${keyPrefix}_rule`
  const polReqRuleId = `policy_request_${keyPrefix}_rule_id`
  const polReqRulePrefix = `policy_request_${keyPrefix}_rule_prefix`

  const { policy_rule_id } = thisPolicy || {}

  const initialValues = {
    action: PolicyAction.ALERT,
    active: true,
    description: '',
    lowest_fare: false,
    operator: PolicyOperator.LESS_THAN_OR_EQUAL_TO,
    [polReqRuleId]: 'new',
    [polReqRulePrefix]: '',
    policy_rule_id,
    value: 0
  } as any

  const dataSource = []

  //@ts-ignore
  const hasItem = thisPolicy[polReqRule]
  if (hasItem) {
    dataSource[0] = hasItem
  }

  const operators = [
    {
      label: t('common.condition.less-than'),
      value: '<'
    },
    {
      label: t('common.condition.less-than-or-equal'),
      value: '<='
    },
    {
      label: t('common.condition.equal'),
      value: '=='
    },
    {
      label: t('common.condition.greater-than-or-equal'),
      value: '>'
    },
    {
      label: t('common.condition.greater-than'),
      value: '>='
    }
  ]

  const tAlert = t('web.policies.action.alert')
  const tBlock = t('web.policies.action.block')
  const tYes = t('common.actions.yes')
  const tNo = t('common.actions.no')
  const tAction = t('web.policies.action.title')
  /**
   * web.policies.rules.services.modal-title-air
   * web.policies.rules.services.modal-title-hotel
   * web.policies.rules.services.modal-title-vehicle
   */
  const tModalTitle = t(`web.policies.rules.services.modal-title-${keyPrefix}`)
  /**
   * web.policies.rules.services.title-air
   * web.policies.rules.services.title-hotel
   * web.policies.rules.services.title-vehicle
   */
  const tTitle = `web.policies.rules.services.title-${keyPrefix}`

  function add() {
    refFormik.current?.resetForm(initialValues)
  }

  function handleSubmit(values: typeof initialValues) {
    refFormik.current?.resetForm({})

    formikProps.setFieldValue(
      `policy_rules[${policyRuleIndex}][${polReqRule}]`,
      values
    )
  }

  return (
    <>
      <Title level={4} title={tTitle} />
      <CSS.RuleContainer>
        <CSS.List
          bordered
          dataSource={dataSource}
          header={
            <Row>
              <Col span={4}>
                <Typography.Text strong>
                  {t('web.policies.rules.prefix')}
                </Typography.Text>
              </Col>
              <Col span={6}>
                <Typography.Text strong>
                  {t('web.policies.rules.description')}
                </Typography.Text>
              </Col>
              <CSS.Col span={3}>
                <Typography.Text strong>
                  {t('web.policies.rules.services.lowest-fare')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={3}>
                <Typography.Text strong>
                  {t('common.condition.title')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={3}>
                <Typography.Text strong>
                  {t('web.policies.rules.value')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={3}>
                <Typography.Text strong>{tAction}</Typography.Text>
              </CSS.Col>
              <CSS.Col span={2}>
                <Typography.Text strong>{t('common.active')}</Typography.Text>
              </CSS.Col>
            </Row>
          }
          locale={{ emptyText: <ButtonAddNew onClick={add} /> }}
          renderItem={(item: any) => {
            const ruleId = item[`policy_request_${keyPrefix}_rule_id`]
            const rulePrefix = item[`policy_request_${keyPrefix}_rule_prefix`]

            return (
              <Item
                key={ruleId}
                onClick={() => {
                  refFormik.current?.resetForm(item)
                }}
              >
                <Col span={4}>
                  <RecordText text={rulePrefix} />
                </Col>
                <Col span={6}>
                  <RecordText text={item?.description} />
                </Col>
                <CSS.Col span={3}>
                  <SquareIcon square={item?.lowest_fare} />
                </CSS.Col>
                <CSS.Col span={3}>{t(tOperator(item.operator))}</CSS.Col>
                <CSS.Col span={3}>{item.value || 0}</CSS.Col>
                <CSS.Col span={3}>
                  <PolicyRuleAlertIcon
                    action={item.action}
                    alert={tAlert}
                    block={tBlock}
                  />
                </CSS.Col>
                <CSS.Col onClick={event => event.stopPropagation()} span={2}>
                  <Checkbox
                    defaultChecked={Boolean(item?.active)}
                    onChange={value => {
                      const { checked } = value.target
                      formikProps.setFieldValue(
                        `policy_rules.${policyRuleIndex}.policy_request_${keyPrefix}_rule.active`,
                        checked
                      )
                    }}
                  />
                </CSS.Col>
              </Item>
            )
          }}
        />
      </CSS.RuleContainer>
      <Formik
        initialValues={null}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={(editLimit: FormikProps<typeof initialValues>) => {
          const visible = Boolean(
            editLimit?.values?.policy_request_air_rule_id ||
              editLimit?.values?.policy_request_hotel_rule_id ||
              editLimit?.values?.policy_request_vehicle_rule_id
          )

          return (
            <Modal
              centered
              closable
              mask
              maskClosable
              onCancel={() => {
                refFormik.current?.resetForm({})
              }}
              onOk={() => {
                //validate?
                refFormik.current?.handleSubmit()
              }}
              title={tModalTitle}
              visible={visible}
              width={700}
            >
              <Row gutter={24}>
                <Col span={6}>
                  <Input
                    data-test-id="policy-request-rule-prefix"
                    formikProps={editLimit}
                    label="web.policies.rules.prefix"
                    name={polReqRulePrefix}
                    placeholder="web.policies.rules.prefix-placeholder"
                  />
                </Col>
                <Col span={18}>
                  <Input
                    data-test-id="policy-request-rule-description"
                    formikProps={editLimit}
                    label="web.policies.rules.description"
                    name="description"
                    placeholder="web.policies.rules.description-placeholder"
                    required={false}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={10}>
                  <Form.Item
                    colon={false}
                    label={t('web.policies.rules.services.lowest-fare-modal')}
                  >
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={e => {
                        const { value } = e.target
                        editLimit.setFieldValue('lowest_fare', value)
                      }}
                      size="small"
                      value={editLimit.values?.lowest_fare}
                    >
                      <Radio.Button value={true}>{tYes}</Radio.Button>
                      <Radio.Button value={false}>{tNo}</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Select
                    dataTestId="operator-list"
                    formikProps={editLimit}
                    label="common.condition.title"
                    name="operator"
                    options={operators}
                    required={false}
                  />
                </Col>
                <Col span={7}>
                  <InputCurrency
                    dataTestId="policy-limit"
                    formikProps={editLimit}
                    label="web.policies.rules.value"
                    name="value"
                    required={false}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item colon={false} label={tAction}>
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={e => {
                        let policyAction: PolicyAction = PolicyAction.ALERT
                        if (e.target.value === 'BLOCK') {
                          policyAction = PolicyAction.BLOCK
                        }

                        editLimit.setFieldValue('action', policyAction)
                      }}
                      size="small"
                      value={editLimit.values?.action}
                    >
                      <Radio.Button value="ALERT">{tAlert}</Radio.Button>
                      <Radio.Button value="BLOCK">{tBlock}</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Modal>
          )
        }}
        validationSchema={object().shape({
          [polReqRulePrefix]: string().required('common.required'),
          operator: string()
            .required('common.required')
            .typeError('common.required')
        })}
      />
    </>
  )
}
